/**
 * flowsService — admin-side wrappers around the
 * /api/v1/admin/ion-selection-flows endpoints (the "AI Flows" editor).
 *
 * The backend owns the YAML serde, so these speak STRUCTURED specs (parsed
 * profile dicts) — the UI never touches YAML. Each function returns parsed JSON
 * on success and throws on non-2xx. Validation 400s carry a structured
 * { errors, warnings } detail, which we surface as a readable message plus a
 * `.detail` field for the editor to render inline.
 */
import { authenticatedFetch } from './authService.js'
import { getApiBase } from '../components/admin/analytics/shared/formatters.js'

const BASE = () => `${getApiBase()}/admin/ion-selection-flows`

async function jsonOrThrow(res) {
  if (res.ok) return await res.json()
  let detail = `HTTP ${res.status}`
  let structured = null
  try {
    const body = await res.json()
    if (body?.detail && typeof body.detail === 'object') {
      structured = body.detail
      const errs = Array.isArray(body.detail.errors) ? body.detail.errors : []
      detail = errs.length ? errs.join(' ') : JSON.stringify(body.detail)
    } else if (body?.detail) {
      detail = body.detail
    }
  } catch {
    /* not JSON — keep the status text */
  }
  const err = new Error(detail)
  if (structured) err.detail = structured
  throw err
}

export async function listFlows({ includeInactive = true } = {}) {
  const params = new URLSearchParams({
    include_inactive: includeInactive ? 'true' : 'false',
  })
  const res = await authenticatedFetch(`${BASE()}?${params}`)
  return jsonOrThrow(res)
}

export async function getFlow(slug) {
  const res = await authenticatedFetch(`${BASE()}/${encodeURIComponent(slug)}`)
  return jsonOrThrow(res)
}

export async function listFlowMetrics() {
  const res = await authenticatedFetch(`${BASE()}/metrics`)
  return jsonOrThrow(res)
}

export async function validateFlow(spec) {
  const res = await authenticatedFetch(`${BASE()}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ spec }),
  })
  return jsonOrThrow(res)
}

export async function createFlow({ slug, spec, title, summary }) {
  const res = await authenticatedFetch(BASE(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, spec, title, summary }),
  })
  return jsonOrThrow(res)
}

export async function updateFlow(slug, { spec, title, summary, enabled } = {}) {
  const res = await authenticatedFetch(`${BASE()}/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ spec, title, summary, enabled }),
  })
  return jsonOrThrow(res)
}

/**
 * Ask what a pasted MATLAB script would change. Reads only — nothing is written
 * until the caller passes the returned `spec` to `updateFlow`, which is the same
 * door a hand edit goes through (approval gate included). That is why there is no
 * `applyMatlabImport`: the import has no write path of its own.
 *
 * Resolves to `{ spec, changes: [{path, before, after}], warnings, errors,
 * required_requirements }`. A script the importer cannot read is a 400 whose
 * message names what it did not understand — surfaced by jsonOrThrow as-is,
 * because that message is written for the person doing the import.
 */
export async function previewMatlabImport(slug, { script, filename } = {}) {
  const res = await authenticatedFetch(
    `${BASE()}/${encodeURIComponent(slug)}/matlab-preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ script, filename: filename || null }),
    }
  )
  return jsonOrThrow(res)
}

export async function setFlowActive(slug, enabled) {
  const res = await authenticatedFetch(`${BASE()}/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
  return jsonOrThrow(res)
}
